import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * Свежесть данных СКУД, которую страница даёт плашке в шапке (FR-053): страница площадки показывает свою площадку, а не
 * объект пары. `pending` — площадка ещё не загрузилась или не открылась: плашки нет, время объекта пары за время площадки
 * не выдаётся.
 */
export type PageFreshness = { site: string; tzOffset: string; lastPassAt: string | null } | 'pending';

const CrumbsContext = createContext<{ crumbs: Crumb[]; setCrumbs: (c: Crumb[]) => void }>({
  crumbs: [],
  setCrumbs: () => {},
});
const FreshnessContext = createContext<{
  freshness: PageFreshness | null;
  setFreshness: (f: PageFreshness | null) => void;
}>({ freshness: null, setFreshness: () => {} });

export function CrumbsProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [freshness, setFreshness] = useState<PageFreshness | null>(null);
  return (
    <CrumbsContext.Provider value={{ crumbs, setCrumbs }}>
      <FreshnessContext.Provider value={{ freshness, setFreshness }}>{children}</FreshnessContext.Provider>
    </CrumbsContext.Provider>
  );
}

export function useCrumbs() {
  return useContext(CrumbsContext).crumbs;
}

/** Страница сообщает свои хлебные крошки шапке каркаса (дизайн-система, «Каркас экрана»). */
export function useSetCrumbs(crumbs: Crumb[]) {
  const { setCrumbs } = useContext(CrumbsContext);
  const key = JSON.stringify(crumbs);
  // biome-ignore lint/correctness/useExhaustiveDependencies: сравнение по содержимому через key
  useEffect(() => {
    setCrumbs(crumbs);
  }, [key, setCrumbs]);
}

/** Свежесть страницы для плашки в шапке; `null` — у страницы своей нет, плашка показывает объект пары. */
export function usePageFreshness() {
  return useContext(FreshnessContext).freshness;
}

/** Страница сообщает шапке свежесть своей площадки; уход со страницы возвращает плашке объект пары. */
export function useSetPageFreshness(freshness: PageFreshness) {
  const { setFreshness } = useContext(FreshnessContext);
  const key = JSON.stringify(freshness);
  // biome-ignore lint/correctness/useExhaustiveDependencies: сравнение по содержимому через key
  useEffect(() => {
    setFreshness(freshness);
    return () => setFreshness(null);
  }, [key, setFreshness]);
}
